import { Injectable } from "@angular/core";
import { fromArrayBuffer, type GeoTIFFImage } from "geotiff";

// Viridis colormap key stops: [t, r, g, b]
const VIRIDIS: Array<[number, number, number, number]> = [
  [0.0, 68, 1, 84],
  [0.125, 70, 50, 127],
  [0.25, 54, 92, 141],
  [0.375, 39, 127, 142],
  [0.5, 31, 161, 135],
  [0.625, 74, 194, 109],
  [0.75, 159, 218, 58],
  [0.875, 222, 227, 24],
  [1.0, 253, 231, 37],
];

function viridisColor(t: number): [number, number, number] {
  const v = Math.max(0, Math.min(1, t));
  let lo = VIRIDIS[0];
  let hi = VIRIDIS[VIRIDIS.length - 1];
  for (let i = 0; i < VIRIDIS.length - 1; i++) {
    if (v >= VIRIDIS[i][0] && v <= VIRIDIS[i + 1][0]) {
      lo = VIRIDIS[i];
      hi = VIRIDIS[i + 1];
      break;
    }
  }
  const span = hi[0] - lo[0];
  const f = span > 0 ? (v - lo[0]) / span : 0;
  return [
    Math.round(lo[1] + f * (hi[1] - lo[1])),
    Math.round(lo[2] + f * (hi[2] - lo[2])),
    Math.round(lo[3] + f * (hi[3] - lo[3])),
  ];
}

/** Target size of each parallel range segment, and how many range fetches may be in flight at once. */
const RANGE_SEGMENT_BYTES = 8 * 1024 * 1024;
const MAX_PARALLEL_RANGES = 6;

/**
 * Fetches one `start`-`end` byte range into `out` (writing at offset `start`), repeating requests
 * until the range is complete — some backend endpoints cap every response at ~64 KB regardless of
 * the requested range size.
 */
async function fetchRangeInto(url: string, start: number, end: number, out: Uint8Array): Promise<void> {
  let offset = start;
  while (offset <= end) {
    const response = await fetch(url, { headers: { Range: `bytes=${offset}-${end}` } });
    if (response.status !== 206) {
      throw new Error(`Unexpected status ${response.status} for Range request bytes=${offset}-${end}`);
    }
    const chunk = new Uint8Array(await response.arrayBuffer());
    if (chunk.byteLength === 0) throw new Error(`Empty Range response for bytes=${offset}-${end}`);
    const usable = chunk.byteLength > end - offset + 1 ? chunk.subarray(0, end - offset + 1) : chunk;
    out.set(usable, offset);
    offset += usable.byteLength;
  }
}

/**
 * Fetches a URL's full body, working around backend endpoints that respond with
 * 206 Partial Content (and only the first chunk, e.g. 64 KB) even though the request carries no
 * Range header — reads the real total size off Content-Range and fetches the remainder in
 * parallel range segments (each repeated until filled, since responses may be capped at ~64 KB).
 * A normal 200 response is returned as-is, in a single request.
 */
export async function fetchFullArrayBuffer(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url);
  const firstChunk = new Uint8Array(await response.arrayBuffer());
  if (response.status !== 206) {
    if (firstChunk.byteLength === 0) throw new Error("Empty response body");
    return firstChunk.buffer;
  }

  // Partial response — the backend's 206 quirk. Without a total in Content-Range there is
  // nothing more to request; treat what we got as everything (same as the old loop did).
  const total = Number(response.headers.get("Content-Range")?.match(/\/(\d+)$/)?.[1]) ?? 0;
  if (!total || total <= firstChunk.byteLength) return firstChunk.buffer;

  const out = new Uint8Array(total);
  out.set(firstChunk, 0);

  const segments: Array<[number, number]> = [];
  for (let start = firstChunk.byteLength; start < total; start += RANGE_SEGMENT_BYTES) {
    segments.push([start, Math.min(start + RANGE_SEGMENT_BYTES, total) - 1]);
  }
  let nextSegment = 0;
  const workers = Array.from({ length: Math.min(MAX_PARALLEL_RANGES, segments.length) }, async () => {
    while (nextSegment < segments.length) {
      const [start, end] = segments[nextSegment++];
      await fetchRangeInto(url, start, end, out);
    }
  });
  await Promise.all(workers);
  return out.buffer;
}

/** Decodes a base64 string to an ArrayBuffer. */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i);
  return buf.buffer;
}

const NO_DATA_VALUES = new Set([-9999, -32768, -3.4028234663852886e38]);

/** Band value range, plus the nodata sentinel actually found in the data (if any). */
interface BandStats {
  min: number;
  max: number;
  nodata?: number;
}

/**
 * Scans a raw band for its real value range, excluding known nodata sentinel values
 * (elevation rasters commonly use -9999, -32768, or the float32 min as a "no data" marker —
 * without excluding these, min/max would span the sentinel's extreme value instead of the
 * actual data range).
 */
function computeBandStats(band: Float32Array | Int16Array | Uint16Array | Uint8Array, knownNodata?: number): BandStats {
  const isNoData = (v: number) => NO_DATA_VALUES.has(v) || (knownNodata !== undefined && v === knownNodata);
  let min = Infinity;
  let max = -Infinity;
  let nodata: number | undefined;
  for (let i = 0; i < band.length; i++) {
    const v = band[i];
    if (nodata === undefined && isNoData(v)) {
      nodata = v;
      continue;
    }
    if (isFinite(v) && !isNoData(v)) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  return { min, max, nodata };
}

/** Max number of tiles to decode for the sampled band stats (see computeSampledStats). */
const MAX_STATS_TILES = 64;

/**
 * Band value range WITHOUT decoding the whole raster: backend DSM/DTM extracts are often a
 * single full-resolution image (no overviews), where a full readRasters() decodes every deflate
 * tile on the main thread — measured ~9.5 s of UI freeze for a 164 MB file. Instead decodes a
 * spread of individual tiles (aligned to the tile grid so each read is exactly one tile) and
 * computes the range over those; images with fewer tiles than the budget decode fully, and
 * stripped images degrade to full-width strip reads. Measured ~0.7 s for the same file.
 * Nodata comes from the file's own GDAL_NODATA tag (exact, header-only), with the sentinel scan
 * in computeBandStats as fallback.
 * Tradeoff: the sampled range may clip narrow outliers at the extremes (a few % of the stretch).
 */
async function computeSampledStats(image: GeoTIFFImage): Promise<BandStats> {
  const gdalNodata = image.getGDALNoData();
  let nodata: number | undefined = gdalNodata !== null && isFinite(gdalNodata) ? gdalNodata : undefined;

  const width = image.getWidth();
  const height = image.getHeight();
  const tileWidth = image.getTileWidth();
  const tileHeight = image.getTileHeight();
  const tilesAcross = Math.ceil(width / tileWidth);
  const tilesDown = Math.ceil(height / tileHeight);
  const step = Math.max(1, Math.ceil(Math.sqrt((tilesAcross * tilesDown) / MAX_STATS_TILES)));

  let min = Infinity;
  let max = -Infinity;
  for (let ty = 0; ty < tilesDown; ty += step) {
    for (let tx = 0; tx < tilesAcross; tx += step) {
      const rasters = await image.readRasters({
        window: [
          tx * tileWidth,
          ty * tileHeight,
          Math.min((tx + 1) * tileWidth, width),
          Math.min((ty + 1) * tileHeight, height),
        ],
      });
      const stats = computeBandStats(rasters[0] as Float32Array | Int16Array | Uint16Array | Uint8Array, nodata);
      if (stats.min < min) min = stats.min;
      if (stats.max > max) max = stats.max;
      if (nodata === undefined && stats.nodata !== undefined) nodata = stats.nodata;
    }
  }
  return { min, max, nodata };
}

const TIFF_LITTLE_ENDIAN_MARKER = 0x4949; // 'II'
const TIFF_MAGIC = 42;
const DOUBLE_FIELD_TYPE = 12; // TIFF field type: IEEE double (8 bytes)
const MODEL_TIEPOINT_TAG = 33922;
const MODEL_PIXEL_SCALE_TAG = 33550;

/** Byte offset of a tag's out-of-line data within the IFD entry at `entryOffset`, or null if the tag isn't found or isn't shaped as expected. */
function findTagDataOffset(
  view: DataView,
  ifdOffset: number,
  numEntries: number,
  tag: number,
  fieldType: number,
  minCount: number,
  littleEndian: boolean,
): number | null {
  for (let i = 0; i < numEntries; i++) {
    const entryOffset = ifdOffset + 2 + i * 12;
    if (view.getUint16(entryOffset, littleEndian) !== tag) continue;
    if (view.getUint16(entryOffset + 2, littleEndian) !== fieldType) return null;
    if (view.getUint32(entryOffset + 4, littleEndian) < minCount) return null;
    return view.getUint32(entryOffset + 8, littleEndian);
  }
  return null;
}

/**
 * Locates a GeoTIFF's ModelTiepointTag and, if it references a raster pixel other than (0,0),
 * rewrites it (in a copy of the buffer) to reference pixel (0,0) instead.
 *
 * This works around a real gap in geotiff.js: `Image#getOrigin()` always treats a tie point's
 * world x/y as the pixel-(0,0) corner, regardless of which raster pixel the tie point actually
 * references (the GeoTIFF spec allows a tie point to reference any pixel). `ol/source/GeoTIFF`
 * relies on that method, so a file whose tie point references e.g. its center pixel gets its
 * whole extent shifted by (tiePointCol * pixelWidth, -tiePointRow * pixelHeight) — GDAL/QGIS
 * read the same file correctly since they do account for the tie point's own row/col.
 *
 * Returns the buffer unchanged (same reference) if no correction is needed, the tag can't be
 * found, or the file isn't a plain (non-BigTIFF) TIFF this walker understands.
 */
function correctTiePointOrigin(buffer: ArrayBuffer): { buffer: ArrayBuffer; corrected: boolean } {
  const view = new DataView(buffer);
  if (view.byteLength < 8) return { buffer, corrected: false };

  const littleEndian = view.getUint16(0) === TIFF_LITTLE_ENDIAN_MARKER;
  if (view.getUint16(2, littleEndian) !== TIFF_MAGIC) return { buffer, corrected: false }; // not classic TIFF (e.g. BigTIFF)

  let ifdOffset = view.getUint32(4, littleEndian);
  while (ifdOffset) {
    const numEntries = view.getUint16(ifdOffset, littleEndian);
    const tiePointOffset = findTagDataOffset(view, ifdOffset, numEntries, MODEL_TIEPOINT_TAG, DOUBLE_FIELD_TYPE, 6, littleEndian);

    if (tiePointOffset !== null) {
      const tiePointI = view.getFloat64(tiePointOffset, littleEndian);
      const tiePointJ = view.getFloat64(tiePointOffset + 8, littleEndian);
      if (tiePointI === 0 && tiePointJ === 0) return { buffer, corrected: false }; // already anchored at pixel (0,0)

      const scaleOffset = findTagDataOffset(view, ifdOffset, numEntries, MODEL_PIXEL_SCALE_TAG, DOUBLE_FIELD_TYPE, 2, littleEndian);
      if (scaleOffset === null) return { buffer, corrected: false }; // can't compute the correction without pixel scale

      const scaleX = view.getFloat64(scaleOffset, littleEndian);
      const scaleY = view.getFloat64(scaleOffset + 8, littleEndian);
      const worldX = view.getFloat64(tiePointOffset + 24, littleEndian);
      const worldY = view.getFloat64(tiePointOffset + 32, littleEndian);

      const corrected = buffer.slice(0);
      const correctedView = new DataView(corrected);
      correctedView.setFloat64(tiePointOffset, 0, littleEndian); // i
      correctedView.setFloat64(tiePointOffset + 8, 0, littleEndian); // j
      correctedView.setFloat64(tiePointOffset + 16, 0, littleEndian); // k
      correctedView.setFloat64(tiePointOffset + 24, worldX - tiePointI * scaleX, littleEndian); // x
      correctedView.setFloat64(tiePointOffset + 32, worldY + tiePointJ * scaleY, littleEndian); // y
      return { buffer: corrected, corrected: true };
    }
    ifdOffset = view.getUint32(ifdOffset + 2 + numEntries * 12, littleEndian);
  }
  return { buffer, corrected: false };
}

@Injectable({ providedIn: "root" })
export class GeoTiffRendererService {
  /**
   * Decodes a base64 GeoTIFF, applies the viridis colormap, and draws the result
   * onto the provided canvas. Returns image dimensions for layout purposes.
   */
  async renderToCanvas(base64: string, canvas: HTMLCanvasElement): Promise<{ width: number; height: number }> {
    const arrayBuffer = base64ToArrayBuffer(base64);
    const tiff = await fromArrayBuffer(arrayBuffer);
    const image = await tiff.getImage();
    const width = image.getWidth();
    const height = image.getHeight();
    const rasters = await image.readRasters();
    const band = rasters[0] as Float32Array | Int16Array | Uint16Array | Uint8Array;
    const { min, max } = computeBandStats(band);
    const range = max > min ? max - min : 1;

    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D context unavailable");

    const imgData = ctx.createImageData(width, height);
    const d = imgData.data;
    for (let i = 0; i < band.length; i++) {
      const v = band[i];
      const t = isFinite(v) && !NO_DATA_VALUES.has(v) ? (v - min) / range : 0;
      const [r, g, b] = viridisColor(t);
      const px = i * 4;
      d[px] = r;
      d[px + 1] = g;
      d[px + 2] = b;
      d[px + 3] = 255;
    }
    ctx.putImageData(imgData, 0, 0);
    return { width, height };
  }

  /** Triggers a browser download of the raw GeoTIFF bytes from base64. */
  downloadBase64(base64: string, filename = "data.tif"): void {
    const link = document.createElement("a");
    link.href = `data:image/tiff;base64,${base64}`;
    link.download = filename;
    link.click();
  }

  /**
   * Prepares a GeoTIFF for use as a map layer: fetches it, works around a georeferencing bug
   * found in some backend DSM/DTM extracts (see correctTiePointOrigin) if present, and computes
   * the band value range for the WebGL stretch (see computeBandStats — `ol/source/GeoTIFF` has
   * no way to derive this itself unless the file carries embedded GDAL statistics tags, without
   * which the layer normalizes against the full data-type range and renders solid black).
   *
   * Returns the (possibly corrected) file as a Blob — persisted to IndexedDB as-is so the layer
   * survives a page reload, rather than a blob: object URL, which only lives for the current page —
   * plus the band stats. Returns null if the file couldn't be read.
   */
  async prepareForMap(url: string): Promise<{ blob: Blob; min: number; max: number; nodata?: number } | null> {
    try {
      const originalBuffer = await fetchFullArrayBuffer(url);
      const { buffer, corrected } = correctTiePointOrigin(originalBuffer);

      const tiff = await fromArrayBuffer(buffer);
      // Sample the band stats on the lowest-resolution image (smallest overview when the file
      // has overviews; for the single-image backend extracts this is image 0) — see
      // computeSampledStats for why a full-resolution readRasters() is too slow here.
      let image = await tiff.getImage();
      const imageCount = await tiff.getImageCount();
      for (let i = 1; i < imageCount; i++) {
        const candidate = await tiff.getImage(i);
        if (candidate.getWidth() < image.getWidth()) image = candidate;
      }
      const stats = await computeSampledStats(image);
      if (!isFinite(stats.min) || !isFinite(stats.max)) return null;

      if (corrected) {
        console.warn("GeoTiffRendererService: corrected a GeoTIFF tie point that didn't reference pixel (0,0)", url);
      }
      return { blob: new Blob([buffer], { type: "image/tiff" }), ...stats };
    } catch (err) {
      console.error("GeoTiffRendererService: failed to prepare GeoTIFF for map", url, err);
      return null;
    }
  }
}
